import { apiService } from './apiService';

export interface AutomationRule {
  id: string;
  conversationId: string;
  isEnabled: boolean;
  instructions: string;
  goal: string;
  tone: 'professional' | 'friendly' | 'casual' | 'empathetic' | 'custom';
  customTone?: string;
  maxResponses: number;
  stopKeywords?: string[];
  humanKeywords?: string[];
  sentimentThreshold: 'negative' | 'very_negative' | 'any';
  complexityThreshold: 'medium' | 'high' | 'very_high';
  activeStartTime: string;
  activeEndTime: string;
  timezone: string;
  activeWeekdays: number[];
  minDelayMinutes: number;
  maxDelayMinutes: number;
  contextWindow: number;
  confidenceThreshold: number;
  escalateOnUncertainty: boolean;
  allowFollowUp: boolean;
  currentResponseCount: number;
  lastAutomatedResponseAt?: Date;
  isCurrentlyActive: boolean;
}

export interface AutomationExecutionLog {
  id: string;
  conversationId: string;
  triggeredAt: string;
  scheduledFor: string;
  scheduledDelayMinutes: number;
  executionStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped' | 'escalated' | 'paused';
  generatedResponse?: string;
  finalResponse?: string;
  automationRule?: Partial<AutomationRule>;
  createdAt: string;
  metadata?: any;
}

export interface AutomationConfig {
  isEnabled: boolean;
  wasAlreadyEnabled?: boolean;
  activationType?: 'immediate' | 'on_response';
  activateNow?: boolean;
  instructions: string;
  goal: string;
  tone: 'professional' | 'friendly' | 'casual' | 'empathetic' | 'custom';
  customTone?: string;
  stopConditions: {
    maxResponses: number;
    stopKeywords: string[];
    humanKeywords: string[];
    sentimentThreshold: 'negative' | 'very_negative' | 'any';
    complexityThreshold: 'medium' | 'high' | 'very_high';
  };
  timing: {
    activeHours: {
      start: string;
      end: string;
      timezone: string;
      weekdays: number[];
    };
    delayRange: {
      min: number;
      max: number;
    };
  };
  advanced: {
    contextWindow: number;
    confidenceThreshold: number;
    escalateOnUncertainty: boolean;
    allowFollowUp: boolean;
  };
}

class AutomationService {
  /**
   * Get automation rule for a conversation
   */
  async getAutomationRule(conversationId: string): Promise<AutomationRule | null> {
    try {
      const response = await apiService.get(`/api/automation/${conversationId}`) as any;
      return response.data as AutomationRule;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Save or update automation rule
   */
  async saveAutomationRule(conversationId: string, config: AutomationConfig): Promise<AutomationRule> {
    const response = await apiService.put(`/api/automation/${conversationId}`, config) as any;
    return response.data;
  }

  /**
   * Delete automation rule
   */
  async deleteAutomationRule(conversationId: string): Promise<void> {
    await apiService.delete(`/api/automation/${conversationId}`);
  }

  /**
   * Get execution logs for a conversation
   */
  async getExecutionLogs(
    conversationId: string, 
    options?: { 
      status?: string; 
      limit?: number 
    }
  ): Promise<AutomationExecutionLog[]> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    
    const response = await apiService.get(
      `/api/automation/${conversationId}/logs?${params.toString()}`
    ) as any;
    return response.data?.logs || [];
  }

  /**
   * Cancel a queued automation response
   */
  async cancelExecution(executionId: string): Promise<void> {
    await apiService.post(`/api/automation/execution/${executionId}/cancel`);
  }

  /**
   * Send a queued automation response immediately
   */
  async sendNow(executionId: string): Promise<void> {
    await apiService.post(`/api/automation/execution/${executionId}/send-now`);
  }

  /**
   * Pause the countdown for a queued automation response
   */
  async pauseExecution(executionId: string): Promise<{ remainingDelayMinutes: number }> {
    const response = await apiService.put(`/api/automation/execution/${executionId}/pause`) as any;
    return response.data;
  }

  /**
   * Resume a paused automation response
   */
  async resumeExecution(executionId: string): Promise<{ scheduledFor: string; delayMinutes: number }> {
    const response = await apiService.put(`/api/automation/execution/${executionId}/resume`) as any;
    return response.data;
  }

  /**
   * Reschedule a queued automation response
   */
  async rescheduleExecution(
    executionId: string, 
    delayMinutes: number
  ): Promise<{ scheduledFor: string; delayMinutes: number }> {
    const response = await apiService.put(`/api/automation/execution/${executionId}/reschedule`, {
      delayMinutes
    }) as any;
    return response.data;
  }

  /**
   * Pause automation for a conversation
   */
  async pauseAutomation(conversationId: string): Promise<void> {
    await apiService.post(`/api/automation/${conversationId}/pause`);
  }

  /**
   * Resume automation for a conversation
   */
  async resumeAutomation(conversationId: string): Promise<void> {
    await apiService.post(`/api/automation/${conversationId}/resume`);
  }

  /**
   * Manually trigger automation for testing
   */
  async triggerAutomation(conversationId: string, message: { content: string; channel?: string }): Promise<AutomationExecutionLog> {
    const response = await apiService.post(`/api/automation/${conversationId}/trigger`, { message }) as any;
    return response.data;
  }

  /**
   * Get system-wide automation status
   */
  async getSystemStatus(): Promise<{
    totalRules: number;
    activeRules: number;
    recentExecutions: number;
    systemHealth: string;
  }> {
    const response = await apiService.get('/api/automation/status') as any;
    return response.data;
  }
}

export const automationService = new AutomationService(); 